import {
  ArrowDown,
  ArrowDownToLine,
  ArrowUp,
  ArrowUpToLine,
} from "lucide-react";

import type { ItemAction } from "./ItemActionsMenu";

export type ReorderDirection = "up" | "down" | "top" | "bottom";

/** Move an item within a copy of the array; returns the same array if no-op. */
export const moveItemInArray = <T>(
  items: T[],
  index: number,
  direction: ReorderDirection
): T[] => {
  if (index < 0 || index >= items.length) return items;

  if (direction === "up") {
    if (index === 0) return items;
    const next = [...items];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    return next;
  }

  if (direction === "down") {
    if (index >= items.length - 1) return items;
    const next = [...items];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    return next;
  }

  if (direction === "top") {
    if (index === 0) return items;
    const next = [...items];
    const [item] = next.splice(index, 1);
    next.unshift(item);
    return next;
  }

  if (index >= items.length - 1) return items;
  const next = [...items];
  const [item] = next.splice(index, 1);
  next.push(item);
  return next;
};

interface ReorderActionsOptions {
  index: number;
  length: number;
  disabled?: boolean;
  onMove: (direction: ReorderDirection) => void;
}

/** Move up / down / top / bottom — only for inline ReorderControls (list Edit Order). */
export const getReorderActions = ({
  index,
  length,
  disabled = false,
  onMove,
}: ReorderActionsOptions): ItemAction[] => {
  const isFirst = index <= 0;
  const isLast = index < 0 || index >= length - 1;

  return [
    {
      id: "move-up",
      label: "Move up",
      icon: ArrowUp,
      disabled: disabled || isFirst,
      onClick: () => onMove("up"),
    },
    {
      id: "move-down",
      label: "Move down",
      icon: ArrowDown,
      disabled: disabled || isLast,
      onClick: () => onMove("down"),
    },
    {
      id: "move-top",
      label: "Move to top",
      icon: ArrowUpToLine,
      disabled: disabled || isFirst,
      onClick: () => onMove("top"),
    },
    {
      id: "move-bottom",
      label: "Move to bottom",
      icon: ArrowDownToLine,
      disabled: disabled || isLast,
      onClick: () => onMove("bottom"),
    },
  ];
};
